import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { Box, useTheme, useMediaQuery, alpha, Typography } from '@mui/material';
import { motion } from 'framer-motion';

ChartJS.register(ArcElement, Tooltip, Legend);

interface CategoryPieChartProps {
  treatmentRecords: Array<{
    id: string;
    treatmentMenu: string;
    totalAmount: number;
  }>;
  height?: number;
}

export const CategoryPieChart: React.FC<CategoryPieChartProps> = ({ 
  treatmentRecords,
  height 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartHeight = height || (isMobile ? 250 : 300);

  const { chartData, totalAmount, topCategory } = useMemo(() => {
    // カテゴリー別の集計
    const categoryTotals = new Map<string, number>();
    let total = 0;

    treatmentRecords.forEach(record => {
      let category = 'その他';
      if (record.treatmentMenu.includes('ヘッドスパ')) {
        category = 'ヘッドスパ';
      } else if (record.treatmentMenu.includes('フェイシャル')) {
        category = 'フェイシャル';
      } else if (record.treatmentMenu.includes('アロマ')) {
        category = 'アロマ';
      } else if (record.treatmentMenu.includes('ボディ')) {
        category = 'ボディケア';
      } else if (record.treatmentMenu.includes('フット')) {
        category = 'フットケア';
      }
      
      categoryTotals.set(category, (categoryTotals.get(category) || 0) + record.totalAmount);
      total += record.totalAmount;
    });

    // カテゴリーを売上順にソート
    const sortedCategories = Array.from(categoryTotals.entries())
      .sort((a, b) => b[1] - a[1]);

    const categoryColors = {
      'ヘッドスパ': theme.palette.primary.main,
      'フェイシャル': theme.palette.secondary.main,
      'アロマ': theme.palette.success.main,
      'ボディケア': theme.palette.info.main,
      'フットケア': theme.palette.warning.main,
      'その他': theme.palette.grey[400],
    };

    const labels = sortedCategories.map(([cat]) => cat);
    const data = sortedCategories.map(([, amount]) => amount);
    const backgroundColor = sortedCategories.map(([cat]) => 
      alpha(categoryColors[cat as keyof typeof categoryColors] || theme.palette.grey[400], 0.85)
    );
    const borderColor = sortedCategories.map(([cat]) => 
      categoryColors[cat as keyof typeof categoryColors] || theme.palette.grey[400]
    );

    const percentages = data.map(amount => ((amount / total) * 100).toFixed(1));

    return {
      chartData: {
        labels: labels.map((label, i) => `${label} (${percentages[i]}%)`),
        datasets: [{
          data,
          backgroundColor,
          borderColor,
          borderWidth: 2,
          hoverOffset: 15,
          spacing: 2,
        }],
      },
      totalAmount: total,
      topCategory: sortedCategories[0]?.[0] || 'なし',
    };
  }, [treatmentRecords, theme]);

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          usePointStyle: true,
          padding: isMobile ? 10 : 15,
          font: {
            size: isMobile ? 11 : 12,
            family: theme.typography.fontFamily,
          },
          color: theme.palette.text.primary,
          generateLabels: (chart) => {
            const data = chart.data;
            if (data.labels && data.datasets) {
              return data.labels.map((label, i) => {
                const dataset = data.datasets[0];
                return {
                  text: label as string,
                  fillStyle: Array.isArray(dataset.backgroundColor) 
                    ? dataset.backgroundColor[i] as string 
                    : '#9e9e9e',
                  strokeStyle: Array.isArray(dataset.borderColor)
                    ? dataset.borderColor[i] as string
                    : '#9e9e9e',
                  lineWidth: 2,
                  hidden: false,
                  index: i,
                };
              });
            }
            return [];
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const total = context.dataset.data.reduce((a, b) => (a as number) + (b as number), 0) as number;
            const percentage = ((value / total) * 100).toFixed(1);
            return `¥${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeInOutQuart',
    },
  };

  return (
    <motion.div
      initial={{ opacity: 0, rotate: -180 }}
      animate={{ opacity: 1, rotate: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
    >
      <Box sx={{ height: chartHeight, position: 'relative' }}>
        <Doughnut options={options} data={chartData} />
        
        {/* 中央に合計金額を表示 */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: isMobile ? '35%' : '30%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            累計売上
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="primary">
            ¥{totalAmount.toLocaleString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            最多: {topCategory}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};